<template>
  <div class="holiday-master">
    <CCard>
      <CCardHeader>
        <strong>祝日マスタ</strong>
        <p class="holiday-master__lead">
          全社共通の祝日です。ここが埋まっていれば、契約企業は祝日を入力しなくて済みます。
          <b>登録が切れると、その翌日から全社の祝日が平日として扱われます。</b>
        </p>
      </CCardHeader>

      <CCardBody>
        <div v-if="loading" class="holiday-master__loading">読み込んでいます…</div>

        <div v-else>
          <!-- ここが本体。取り込む手段より、切れかけに気づけることのほうが効く。 -->
          <section
            class="holiday-master__stock"
            :class="{ 'holiday-master__stock--warn': status.warn }"
          >
            <p class="holiday-master__kicker">STOCK</p>
            <!--
              1件も無いときも、切れているときと同じく、直し方を同じ文で言う
              （「内閣府が公表しているCSVを下の欄から取り込むと解消されます」は、下の切れているときの文と同じ字）。
            -->
            <p v-if="!status.lastDate" class="holiday-master__stock-main">
              <strong>1件も入っていません。</strong>
              現在、全社で祝日が平日として扱われています。内閣府が公表しているCSVを下の欄から取り込むと解消されます。
            </p>
            <!--
              **もう切れているときは、残りの日数を出さない**（2026-10-08）。最後の日を過ぎると
              残りが負になり、「残り -10 日」と出ていた。請求管理の上の警告と同じ判定・同じ言い方に
              する。切れているなら、いますぐ取り込めるので、「内閣府は翌年分を前年の2月頃に…」の
              警告（下）は出さない。
            -->
            <p v-else-if="stockExpired" class="holiday-master__stock-main">
              <strong>{{ status.lastDate }}で登録が切れている</strong>ため、現在、全社で祝日が平日として扱われています。内閣府が公表しているCSVを下の欄から取り込むと解消されます（現在の登録数：全 {{ status.totalCount }} 件）。
            </p>
            <p v-else class="holiday-master__stock-main">
              <strong>{{ status.lastDate }} まで</strong>
              登録済み（残り {{ status.daysRemaining }} 日 / 全 {{ status.totalCount }} 件）。
            </p>
            <!--
              **1件も無いときにも出さない**（2026-10-09、画面で見つけた）。サーバは1件も無いときも
              warn を真で返すので、「1件も入っていません。」のすぐ下に「登録の残りが183日以下に
              なりました。内閣府は翌年分を前年の2月頃に公表します。」が並んでいた。
            -->
            <p v-if="status.warn && status.lastDate && !stockExpired" class="holiday-master__warn">
              <b>登録の残りが{{ status.warnWithinDays }}日以下になりました。</b>
              内閣府は翌年分を前年の2月頃に公表します。下の欄から取り込んでください。
            </p>
          </section>

          <section class="holiday-master__years">
            <p class="holiday-master__kicker">BY YEAR</p>
            <table v-if="status.years && status.years.length" class="holiday-master__table">
              <thead>
                <tr><th>年</th><th>件数</th><th>最初</th><th>最後</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in status.years" :key="row.year">
                  <td>{{ row.year }}</td>
                  <td>{{ row.count }}</td>
                  <td>{{ row.firstDate }}</td>
                  <td>{{ row.lastDate }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else>まだ1件も入っていません。</p>
          </section>

          <section class="holiday-master__import">
            <p class="holiday-master__kicker">IMPORT</p>
            <h3>内閣府のCSVを取り込む</h3>
            <!--
              **前の説明は同じ文の中で食い違っていた**（2026-09-30、束7）。
              「翌年分まで入っているので…数年先まで埋まります」——CSV は翌年分までしか無い。

              **「翌年12月31日まで」も、上の登録状況と食い違っていた**（束7 の見直し）。登録状況は
              最後の祝日の日付（CSV の最後は 11/23 の勤労感謝の日）を出すので、取り込んでも
              12月31日にはならず、「足りない」「失敗した」と迷わせた。同じ物差しで言う。
            -->
            <p>
              内閣府が公表している <code>syukujitsu.csv</code> の中身を貼り付けてください。
              <b>取り込むと、翌年の11月23日ごろ（勤労感謝の日）まで登録されます。年に1回、公表されたあとにお試しと取り込みを行ってください。</b>
            </p>
            <p class="holiday-master__note">
              <b>文字コードは Shift_JIS です。</b>メモ帳やExcelで開いて、
              文字化けしていない状態のものを貼ってください。
              日付が化けることはないので、<b>化けたまま取り込むと休日一覧に化けた名前が並びます。</b>
            </p>

            <textarea
              v-model="csv"
              class="holiday-master__textarea"
              rows="8"
              :placeholder="csvSample"
              :disabled="busy"
            ></textarea>

            <div class="holiday-master__actions">
              <CButton color="secondary" :disabled="busy || !csv.trim()" @click="tryImport">
                試す（登録しない）
              </CButton>
              <!--
                **試した中身だけを取り込める**（2026-09-30、束7）。前は試したあとで貼った文を
                書き換えても押せたままで、試していない中身が全社の休日に入った。
              -->
              <CButton
                color="primary"
                :disabled="busy || !canApply"
                @click="applyImport"
              >
                この内容で取り込む
              </CButton>
            </div>
            <p v-if="editedAfterTry" class="holiday-master__note">
              内容が変更されたため、確認結果をクリアし、［この内容で取り込む］を押せない状態にしています。もう一度［試す（登録しない）］を押すと、取り込めるようになります。
            </p>
            <!--
              **取り込んだ記録の見え先を、押す前に読める所に書く**（束7 の見直し）。
              全社の休日に効くので、誰がいつ何件入れたかを、あとで運営の誰もが確かめられる。
            -->
            <p class="holiday-master__note">
              取り込むと、取り込んだ担当者・日時・件数が記録されます。左メニューの「契約企業一覧」から自社の［管理］を押し、「操作履歴」タブで確認できます。
            </p>

            <div v-if="preview" class="holiday-master__preview">
              <p>
                <strong>{{ preview.parsedCount }} 件</strong>
                読み取れました（{{ preview.firstDate }} 〜 {{ preview.lastDate }}）。
              </p>
              <table class="holiday-master__table">
                <tbody>
                  <tr v-for="(row, i) in preview.sample" :key="i">
                    <td>{{ row.date }}</td>
                    <td>{{ row.name }}</td>
                  </tr>
                </tbody>
              </table>
              <p class="holiday-master__note">先頭と末尾だけを出しています。</p>
            </div>
          </section>

          <p v-if="errorMessage" class="holiday-master__error">{{ errorMessage }}</p>
          <p v-if="successMessage" class="holiday-master__success">{{ successMessage }}</p>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import api from '@/services/nationalHolidayMaster'

export default {
  name: 'NationalHolidayMaster',

  data () {
    return {
      loading: true,
      busy: false,
      csv: '',
      /*
       * 貼り付け欄の見本。属性に「&#10;」と書いても Vue は改行に直さず、記号がそのまま
       * 画面に出ていた。改行の入った文字列で渡す。
       */
      csvSample: ['国民の祝日・休日月日,国民の祝日・休日名称', '2026/1/1,元日', '2026/1/12,成人の日'].join('\n'),
      /* ［試す］が読んだ中身。［この内容で取り込む］はこれを送る（欄の今の中身ではなく）。 */
      triedCsv: '',
      preview: null,
      /* 試したあとで書き換えた。試した結果を消し、もう一度試すよう出す。 */
      editedAfterTry: false,
      status: {},
      errorMessage: '',
      successMessage: ''
    }
  },

  computed: {
    /** 試した結果があり、欄の中身が試したときのままのときだけ取り込める。 */
    canApply () {
      return Boolean(this.preview) && this.csv === this.triedCsv
    },

    /**
     * 登録が、もう切れているか（最後の日が今日より前）。
     *
     * サーバは残りを「今日から最後の日までの日数」で返すので、過ぎると負になる。
     * 請求管理の上の警告（holidayStockExpired）と同じ判定。
     */
    stockExpired () {
      return Boolean(this.status && this.status.lastDate) &&
        Number(this.status.daysRemaining) < 0
    }
  },

  watch: {
    /**
     * 試したあとで書き換えたら、試した結果を消す。
     *
     * **画面に残った件数と先頭・末尾は、書き換える前の中身のもの。**残したまま
     * 取り込めると、確かめた内容と入る内容が違う。
     */
    csv (value) {
      if (this.preview && value !== this.triedCsv) {
        this.preview = null
        this.editedAfterTry = true
      }
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        this.status = await api.status()
      } catch (error) {
        this.errorMessage = '祝日マスタの状態を読み込めませんでした。'
          + ' 時間をおいて開き直してください。'
      } finally {
        this.loading = false
      }
    },

    /**
     * まず試す。
     *
     * **件数だけでは貼り間違いに気づけない。**先頭と末尾を出して、
     * 目で確かめてから入れてもらう。
     */
    async tryImport () {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ''
      this.successMessage = ''
      this.preview = null
      this.editedAfterTry = false
      const tried = this.csv
      try {
        const preview = await api.importCsv(tried, false)
        this.triedCsv = tried
        this.preview = preview
      } catch (error) {
        this.errorMessage = this.messageOf(error)
      } finally {
        this.busy = false
      }
    },

    async applyImport () {
      if (this.busy || !this.canApply) return
      this.busy = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        /* 送るのは試したときの中身。 */
        const result = await api.importCsv(this.triedCsv, true)
        this.successMessage = result.parsedCount + ' 件を取り込みました。'
        this.preview = null
        this.triedCsv = ''
        this.csv = ''
        this.editedAfterTry = false
        await this.load()
      } catch (error) {
        this.errorMessage = this.messageOf(error)
      } finally {
        this.busy = false
      }
    },

    /**
     * サーバの言い分をそのまま出す。
     *
     * **「取り込めませんでした」だけでは何を直せばよいか分からない。**
     * どの行が読めなかったのか、名称が長すぎるのか、サーバが持っている。
     */
    messageOf (error) {
      const body = error && error.response && error.response.data
      if (typeof body === 'string' && body.trim()) return body
      if (body && typeof body.message === 'string' && body.message.trim()) {
        return body.message
      }
      return '取り込めませんでした。CSVファイルの内容をご確認ください。'
    }
  }
}
</script>

<style scoped>
/*
 * 見出しの帯は全画面共通の指定で濃い青・白い字になる。ここで灰色を指定すると、
 * 青の上の灰色でほとんど読めない。帯の字の色をそのまま受け継ぐ。
 */
.holiday-master__lead { margin: 6px 0 0; color: inherit; opacity: .92; }
.holiday-master__kicker {
  margin: 0 0 4px;
  font-size: 12px;
  letter-spacing: .12em;
  color: #8a93a2;
}
.holiday-master__stock {
  padding: 14px 16px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  background: #fbfcfd;
}
.holiday-master__stock--warn { border-color: #f0b849; background: #fff8e8; }
.holiday-master__stock-main { margin: 0; font-size: 15px; }
.holiday-master__warn { margin: 8px 0 0; color: #8a5b00; }
.holiday-master__years { margin-top: 20px; }
.holiday-master__import { margin-top: 24px; }
.holiday-master__table { width: 100%; border-collapse: collapse; }
.holiday-master__table th,
.holiday-master__table td {
  padding: 6px 10px;
  border-bottom: 1px solid #ebedef;
  text-align: left;
}
.holiday-master__textarea {
  width: 100%;
  font-family: monospace;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  padding: 8px;
}
.holiday-master__actions { display: flex; gap: 10px; margin-top: 10px; }
.holiday-master__preview { margin-top: 16px; }
.holiday-master__note { color: #5c6873; font-size: 13px; }
.holiday-master__error { margin-top: 14px; color: #e55353; }
.holiday-master__success { margin-top: 14px; color: #2eb85c; }
</style>
